/**
 * apps/web/src/components/PrivateRoute.tsx
 *
 * Guards the authenticated app. While the boot-time identity probe runs it shows
 * a full-screen spinner; once resolved it either renders the protected tree
 * (inside the app Layout via <Outlet/>) or redirects to /login, preserving the
 * attempted location so we can bounce back after a successful login.
 */

import type React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Spinner } from './ui/Spinner';
import { useStrings } from '../i18n';

export function PrivateRoute(): React.ReactElement {
  const strings = useStrings();
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center gap-3 bg-background text-sm text-muted">
        <Spinner /> {strings.common.loading}
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
